/**
 * Analysis List Page
 * 
 * Displays list of analyses with filtering and creation
 * Requirements: 11.1-11.7, 11A.1-11A.9
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { get, post, del, put } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';

export interface AnalysisListItem {
  id: string;
  name: string;
  ownerId: string;
  visibility: 'public' | 'private';
  isTemplate: boolean;
  createdAt: number;
  updatedAt: number;
}

/**
 * Analysis List Component
 */
export const AnalysisList: React.FC = () => {
  const navigate = useNavigate();
  const [analyses, setAnalyses] = useState<AnalysisListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'public' | 'private' | 'templates'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newAnalysisName, setNewAnalysisName] = useState('');
  const [currentUserId, setCurrentUserId] = useState<string>('');
  const [userStatus, setUserStatus] = useState<'approved' | 'pending' | null>(null);

  useEffect(() => {
    fetchUserStatus();
    fetchAnalyses();
    getCurrentUserId();
  }, []);

  const getCurrentUserId = async () => {
    try {
      const session = await fetchAuthSession();
      const userId = session.tokens?.idToken?.payload.sub as string;
      setCurrentUserId(userId);
    } catch (err) {
      console.error('Error getting user ID:', err);
    }
  };

  const fetchUserStatus = async () => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      console.log('Fetching current user status');

      // Fetch current user details from the /users/me endpoint
      const response = await get({
        apiName: 'SupplyChainAPI',
        path: '/users/me',
        options: {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      }).response;

      const data = await response.body.json() as any;
      console.log('Current user data:', data);
      
      if (data.user) {
        console.log('Setting user status to:', data.user.status);
        setUserStatus(data.user.status);
      } else {
        console.warn('No user data returned');
      }
    } catch (err) {
      console.error('Error fetching user status:', err);
    }
  };

  const fetchAnalyses = async () => {
    try {
      setLoading(true);
      setError(null);

      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      const response = await get({
        apiName: 'SupplyChainAPI',
        path: '/analyses',
        options: {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      }).response;

      const data = await response.body.json() as any;
      setAnalyses(data.analyses || []);
    } catch (err: any) {
      console.error('Error fetching analyses:', err);
      setError('Failed to load analyses');
    } finally {
      setLoading(false);
    }
  };

  const createAnalysis = async () => {
    if (!newAnalysisName.trim()) {
      alert('Please enter an analysis name');
      return;
    }

    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      const response = await post({
        apiName: 'SupplyChainAPI',
        path: '/analyses',
        options: {
          headers: {
            Authorization: `Bearer ${token}`
          },
          body: {
            name: newAnalysisName,
            visibility: 'private',
            isTemplate: false
          }
        }
      }).response;

      const data = await response.body.json() as any;
      setShowCreateModal(false);
      setNewAnalysisName('');
      navigate(`/analyses/${data.id}`);
    } catch (err) {
      console.error('Error creating analysis:', err);
      alert('Failed to create analysis');
    }
  };

  const cloneAnalysis = async (analysisId: string) => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      const response = await post({
        apiName: 'SupplyChainAPI',
        path: `/analyses/${analysisId}/clone`,
        options: {
          headers: {
            Authorization: `Bearer ${token}`
          },
          body: {}
        }
      }).response;

      const data = await response.body.json() as any;
      navigate(`/analyses/${data.id}`);
    } catch (err) {
      console.error('Error cloning analysis:', err);
      alert('Failed to clone analysis');
    }
  };

  const deleteAnalysis = async (analysisId: string) => {
    if (!confirm('Are you sure you want to delete this analysis?')) {
      return;
    }

    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      await del({
        apiName: 'SupplyChainAPI',
        path: `/analyses/${analysisId}`,
        options: {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      }).response;

      fetchAnalyses();
    } catch (err) {
      console.error('Error deleting analysis:', err);
      alert('Failed to delete analysis');
    }
  };

  const toggleVisibility = async (analysisId: string, currentVisibility: string) => {
    const newVisibility = currentVisibility === 'public' ? 'private' : 'public';

    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      await put({
        apiName: 'SupplyChainAPI',
        path: `/analyses/${analysisId}/visibility`,
        options: {
          headers: {
            Authorization: `Bearer ${token}`
          },
          body: { visibility: newVisibility }
        }
      }).response;

      fetchAnalyses();
    } catch (err) {
      console.error('Error updating visibility:', err);
      alert('Failed to update visibility');
    }
  };

  const toggleTemplate = async (analysisId: string, currentIsTemplate: boolean) => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      await put({
        apiName: 'SupplyChainAPI',
        path: `/analyses/${analysisId}/template`,
        options: {
          headers: {
            Authorization: `Bearer ${token}`
          },
          body: { isTemplate: !currentIsTemplate }
        }
      }).response;

      fetchAnalyses();
    } catch (err) {
      console.error('Error updating template status:', err);
      alert('Failed to update template status');
    }
  };

  const filteredAnalyses = analyses.filter(analysis => {
    // Apply filter
    if (filter === 'public' && analysis.visibility !== 'public') return false;
    if (filter === 'private' && (analysis.visibility !== 'private' || analysis.ownerId !== currentUserId)) return false;
    if (filter === 'templates' && !analysis.isTemplate) return false;

    // Apply search
    if (searchQuery && !analysis.name.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }

    return true;
  });

  return (
    <div className="analysis-list-page">
      <div className="page-header">
        <h1>Analyses</h1>
        <button 
          onClick={() => setShowCreateModal(true)} 
          className="primary-button"
          disabled={userStatus === 'pending'}
          title={userStatus === 'pending' ? 'Your account is pending approval' : ''}
        >
          + Create New Analysis
        </button>
      </div>

      {/* Debug info - remove after testing */}
      {process.env.NODE_ENV === 'development' && (
        <div style={{ padding: '10px', backgroundColor: '#f0f0f0', marginBottom: '10px' }}>
          Debug: userStatus = {userStatus || 'null'}
        </div>
      )}

      {/* Pending Approval Banner */}
      {userStatus === 'pending' && (
        <div className="pending-approval-banner" style={{
          backgroundColor: '#fff3cd',
          border: '1px solid #ffc107',
          borderRadius: '4px',
          padding: '16px',
          marginBottom: '20px',
          color: '#856404'
        }}>
          <strong>⏳ Account Pending Approval</strong>
          <p style={{ margin: '8px 0 0 0' }}>
            Your account is pending approval by an administrator.
          </p>
        </div>
      )}

      {loading && <p>Loading analyses...</p>}
      
      {error && <p className="error">{error}</p>}

      {!loading && !error && (
        <>
          {/* Filters */}
          <div className="filters">
        <button 
          className={filter === 'all' ? 'active' : ''}
          onClick={() => setFilter('all')}
        >
          All
        </button>
        <button 
          className={filter === 'public' ? 'active' : ''}
          onClick={() => setFilter('public')}
        >
          Public
        </button>
        <button 
          className={filter === 'private' ? 'active' : ''}
          onClick={() => setFilter('private')}
        >
          My Private
        </button>
        <button 
          className={filter === 'templates' ? 'active' : ''}
          onClick={() => setFilter('templates')}
        >
          Templates
        </button>
        
        <input
          type="text"
          placeholder="Search analyses..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="search-input"
        />
      </div>

      {/* Analysis List */}
      <div className="analysis-list">
        {filteredAnalyses.length === 0 ? (
          <p>No analyses found. Create your first analysis to get started.</p>
        ) : (
          <table className="analysis-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Visibility</th>
                <th>Template</th>
                <th>Updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAnalyses.map(analysis => (
                <tr key={analysis.id}>
                  <td>
                    <button 
                      onClick={() => navigate(`/analyses/${analysis.id}`)}
                      className="link-button"
                    >
                      {analysis.name}
                    </button>
                  </td>
                  <td>
                    <span className={`badge ${analysis.visibility}`}>
                      {analysis.visibility}
                    </span>
                  </td>
                  <td>{analysis.isTemplate ? 'Yes' : 'No'}</td>
                  <td>{new Date(analysis.updatedAt).toLocaleDateString()}</td>
                  <td className="actions">
                    <button onClick={() => navigate(`/analyses/${analysis.id}`)}>
                      Open
                    </button>
                    <button onClick={() => cloneAnalysis(analysis.id)}>
                      Clone
                    </button>
                    {analysis.ownerId === currentUserId && (
                      <>
                        <button onClick={() => toggleVisibility(analysis.id, analysis.visibility)}>
                          {analysis.visibility === 'public' ? 'Make Private' : 'Make Public'}
                        </button>
                        <button onClick={() => toggleTemplate(analysis.id, analysis.isTemplate)}>
                          {analysis.isTemplate ? 'Unmark Template' : 'Mark Template'}
                        </button>
                        <button 
                          onClick={() => deleteAnalysis(analysis.id)}
                          className="danger-button"
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
          </div>
        </>
      )}

      {/* Create Analysis Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Create New Analysis</h2>
            <input
              type="text"
              placeholder="Analysis name"
              value={newAnalysisName}
              onChange={(e) => setNewAnalysisName(e.target.value)}
              className="modal-input"
              autoFocus
            />
            <div className="modal-actions">
              <button onClick={createAnalysis} className="primary-button">
                Create
              </button>
              <button onClick={() => setShowCreateModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalysisList;
